import { useCallback, FC } from "react";
import { DropzoneOptions, useDropzone, FileRejection } from "react-dropzone";
import { useAppDispatch } from "redux/store";
import { showSuccessToast } from "utils";
import { UploadFileResult, FileDropzoneProps } from "./types";
import { showErrorModal, showSummaryModal } from "redux/slices";
import style from "./style.module.scss";
import { useBucketContext } from "context/bucket";

export const FileDropzone: FC<FileDropzoneProps> = ({ children }) => {
  const { uploadFiles, checkFileExist, refreshContents } = useBucketContext();
  const dispatch = useAppDispatch();

  const handleResult = useCallback(
    (uploadResult: UploadFileResult) => {
      const { successFilesUploaded, failureFilesUploaded, existingFiles } =
        uploadResult;
      if (
        successFilesUploaded.length &&
        !failureFilesUploaded.length &&
        !existingFiles.length
      ) {
        showSuccessToast(
          `File caricat${successFilesUploaded.length > 1 ? "i" : "o"}`,
          "success_files_upload"
        );
      } else if (
        !successFilesUploaded.length &&
        failureFilesUploaded.length &&
        !existingFiles.length
      ) {
        dispatch(showErrorModal({ text: "Errore in caricamento dei file" }));
      } else {
        dispatch(
          showSummaryModal({
            type: "error",
            title: "Oh oh",
            successListItem: successFilesUploaded.map(
              (o) => `${o.name}: caricato con successo`
            ),
            failureListItem: [
              ...failureFilesUploaded.map(
                (o) => `${o.name}: errore nel caricamento`
              ),
              ...existingFiles.map((o) => `${o.name}: file già esistente`),
            ],
          })
        );
      }
    },
    [dispatch]
  );

  const handleAcceptedFiles = useCallback(
    async (files: File[]): Promise<UploadFileResult> => {
      const existingFiles: File[] = [];
      const newFiles: File[] = [];

      for (let file of files) {
        const exist = await checkFileExist?.(file);
        exist ? existingFiles.push(file) : newFiles.push(file);
      }

      const result = await uploadFiles?.(newFiles);
      if (result) {
        return { ...result, existingFiles };
      } else {
        return {
          successFilesUploaded: [],
          failureFilesUploaded: [],
          existingFiles,
        };
      }
    },
    [checkFileExist, uploadFiles]
  );

  const handleDropAccepted = async (acceptedFiles: File[]) => {
    const result = await handleAcceptedFiles(acceptedFiles);
    if (result) {
      handleResult(result);
      result?.successFilesUploaded.length && (await refreshContents());
    }
  };

  const handleDrop = (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => {
    const failures = fileRejections?.map((o) => o.file);
    switch (true) {
      case acceptedFiles.length && !fileRejections.length:
        handleDropAccepted(acceptedFiles);
        break;
      case fileRejections.length && !acceptedFiles.length:
        dispatch(
          showErrorModal({
            text: `${failures.map((f) => f.name)}: Il file è in un formato non valido`,
          })
        );
        break;
      case !!acceptedFiles.length && !!fileRejections.length:
        handleDropAccepted(acceptedFiles);
        dispatch(
          showSummaryModal({
            type: "info",
            title: "Oh oh",
            successListItem: acceptedFiles.map(
              (o) => `${o.name}: caricato con successo`
            ),
            failureListItem: [
              ...failures.map(
                (f) => `${f.name}: Il file è in un formato non valido`
              ),
            ],
          })
        );
        break;
      default:
        break;
    }
  };
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    noDrag: false,
    accept: {
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
      "application/pdf": [".pdf"],
      "text/plain": [".txt"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.oasis.opendocument.text": [".odt"],
      "application/vnd.oasis.opendocument.spreadsheet": [".ods"],
    },
  } as DropzoneOptions);

  return (
    <div {...getRootProps({ onClick: (event) => event.stopPropagation() })}>
      <input {...getInputProps()} />
      {isDragActive ? (
        <div className={style.uploader}>
          <div className={`text-light ${style.inner}`}>Rilascia i file qui</div>
        </div>
      ) : (
        <div className="flex-grow-1">{children}</div>
      )}
    </div>
  );
};
